<template>
  <div class="content">
    <!-- 操作 -->
    <div class="operate-group">
      <div class="operate-group-left">
        <!-- 编辑 -->
        <el-button
          type="primary"
          :disabled="checkedRows.length !== 1"
          @click.native="editeFun">
          <svg-icon icon-class="revise"/>
          {{ t('table.modify') }}
        </el-button>
        <!--更多操作-->
        <el-dropdown trigger="click" class="margin-l2">
          <el-button>
            {{ t('table.moreHandle') }}<el-icon class="el-icon--right"><arrow-down /></el-icon>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <!-- 重置密码 -->
              <el-dropdown-item :disabled="checkedRows.length !== 1" @click.native="resetPasswordFun">
                <svg-icon icon-class="resetPassword" class="stroke"/>
                {{ t('user.resetPassword') }}
              </el-dropdown-item>
              <!-- 启用 -->
              <el-dropdown-item
                :disabled="checkedRows.length !== 1 || checkedRows[0].status === 'enabled'"
                divided
                @click.native="editStatusFun">
                <svg-icon icon-class="enable" class="stroke"/>
                {{ t('table.enable') }}
              </el-dropdown-item>
              <!-- 禁用 -->
              <el-dropdown-item :disabled="checkedRows.length !== 1 || checkedRows[0].status === 'disabled'" @click.native="editStatusFun">
                <svg-icon icon-class="disable" class="stroke"/>
                {{ t('table.disable') }}
              </el-dropdown-item>
              <!-- 代管容灾 -->
              <el-dropdown-item
                :loading="delegateLoading"
                :disabled="disabledDelegateDr || delegateLoading"
                divided
                @click.native="delegateFun('dr')">
                <svg-icon icon-class="impersonated" class="stroke"/>
                {{ t('user.delegate_dr') }}
              </el-dropdown-item>
              <!-- 代管迁移 -->
              <el-dropdown-item
                :loading="delegateLoading"
                :disabled="disabledDelegateMigration || delegateLoading"
                @click.native="delegateFun('migration')">
                <svg-icon icon-class="impersonated" class="stroke"/>
                {{ t('user.delegate_migration') }}
              </el-dropdown-item>
              <!-- 删除 -->
              <el-dropdown-item :disabled="checkedRows.length < 1" divided class="emphasize" @click.native="deleteFun">
                <svg-icon icon-class="release" class="stroke"/>
                {{ t('table.delete') }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
      <div class="operate-group-right">
        <search-toolbar
          v-model:quick-field="quickSearchField"
          v-model:quick-value="quickSearchValue"
          :quick-fields="quickSearchFields"
          :default-quick-field="defaultQuickField"
          :search-mode="searchMode"
          :filter-summary="filterStr"
          :advanced-button-text="advancedFilterButtonText"
          :choose-placeholder="t('placeholder.choose')"
          :search-placeholder="t('placeholder.search')"
          :refresh-text="t('table.refresh')"
          @quick-field-change="handleQuickFieldChange"
          @quick-value-change="handleQuickValueChange"
          @quick-input="handleQuickInput"
          @open-advanced="openAdvancedDrawer"
          @reset-search="resetAllSearch"
          @refresh="refreshList" />
      </div>
    </div>
    <!-- 表格 -->
    <custom-table
      v-model:checkedRows="checkedRows"
      :height="$store.getters.windowHeight - 300 < 200 ? 200 : $store.getters.windowHeight - 300"
      :tableData="tableData"
      :isShowCheck="true"
      @getTableData="getList"
    />
    <!-- 编辑用户 -->
    <edit-tenant v-model:add-user-layer="editLayer" :rowData="rowData" :editType="editType" @getList="getList" />
    <advanced-filter-drawer
      v-model:visible="advancedDrawerVisible"
      :title="advancedFilterButtonText"
      width="50%"
      :loading="tenantOptionsLoading"
      :has-other-condition="false"
      :filter-condition="advancedFilterCondition"
      :other-condition-list="advancedOtherConditionList"
      @submit="handleAdvancedSubmit"
      @cancel="handleAdvancedCancel" />
  </div>
</template>

<script setup>
import EditTenant from '../com/editTenant'
import AdvancedFilterDrawer from '@/components/AdvancedFilterDrawer'
import SearchToolbar from '@/components/SearchToolbar'
import { getTenants } from '@/api/users/tenant'
import { getUsers, changeUsersStatus } from '@/api/users/user'
import { getSysConfig } from '@/api/noticeSetting'
import getTableList from '@/composables/getList'
import useListSearch from '@/composables/useListSearch'
import { rolesOptions } from '@/dataJson/users'
import store from '@/store'
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { deleteUsers, getDelegateUserInfo } from '../../../api/users/user'
import {
  createUserAdvancedFilterCondition,
  createUserAdvancedOtherConditionList,
  createUserQuickSearchFields
} from './searchConfig'

const { t } = useI18n()

const defaultQuickField = 'username'

const roleOptions = rolesOptions
// 租户下拉选项：每次进入租户筛选时刷新，同时复用进行中的请求
const tenantOptions = ref([])
const tenantOptionsLoading = ref(false)
let tenantOptionsPromise = null

const sceneMap = {
  'dr': t('license.dr'),
  'migration': t('license.migration')
}

const quickSearchFields = computed(() => createUserQuickSearchFields({
  t,
  roleOptions,
  tenantOptions: tenantOptions.value
}))

const advancedFilterButtonText = computed(() => t('table.advancedFilter'))

const search = reactive({
  roles: '',
  username: '',
  filter_email: '',
  user_ids: '',
  enterprise_id: '',
  enterprise_name: '',
  scene: '',
  status: ''
})
// 获取列表
const { listData, listTotal, getList, searchFun } = getTableList(search, getUsers)

const checkedRows = ref([])
// 编辑
const editLayer = ref(false)
const rowData = ref({})
const editType = ref('')

const loadTenantOptions = async() => {
  if (tenantOptionsPromise) return tenantOptionsPromise

  tenantOptionsLoading.value = true
  tenantOptionsPromise = (async() => {
    try {
      const res = await getTenants({
        page: 1,
        page_size: 99999
      })
      tenantOptions.value = res.data.pages.map(item => ({
        label: `${item.name}(${item.id})`,
        value: item.id
      }))
    } catch (error) {
      tenantOptions.value = []
    } finally {
      tenantOptionsLoading.value = false
      tenantOptionsPromise = null
    }
  })()

  return tenantOptionsPromise
}

const {
  searchMode,
  quickSearchField,
  quickSearchValue,
  advancedDrawerVisible,
  filterStr,
  advancedFilterCondition,
  resetAllSearch,
  handleQuickFieldChange,
  handleQuickValueChange,
  handleQuickInput,
  openAdvancedDrawer,
  handleAdvancedSubmit,
  handleAdvancedCancel
} = useListSearch({
  search,
  searchFun,
  quickFields: quickSearchFields,
  defaultQuickField,
  createAdvancedFilterCondition: () => createUserAdvancedFilterCondition({
    t,
    roleOptions,
    tenantOptions: tenantOptions.value
  }),
  onQuickFieldChange: async(field) => {
    if (field === 'enterprise_id') await loadTenantOptions()
  },
  onOpenAdvanced: loadTenantOptions
})

const advancedOtherConditionList = computed(() => createUserAdvancedOtherConditionList())

const refreshList = () => {
  getList()
}

const editeFun = () => {
  rowData.value = checkedRows.value[0]
  editType.value = 'editUser'
  editLayer.value = true
}
const delegateLoading = ref(false) // 代管loading

// 是否禁用代管容灾
const disabledDelegateDr = computed(() => {
  if (checkedRows.value.length !== 1) return true
  const { scene, status } = checkedRows.value[0]
  // 状态不是 enabled 时禁用，或者 scene 不符合要求时禁用
  return status !== 'enabled' || !(scene === 'dr' || !scene)
})

// 是否禁用代管迁移
const disabledDelegateMigration = computed(() => {
  if (checkedRows.value.length !== 1) return true
  const { scene, status } = checkedRows.value[0]
  // 状态不是 enabled 时禁用，或者 scene 不符合要求时禁用
  return status !== 'enabled' || !(scene === 'migration' || !scene)
})

// 状态更改
const editStatusFun = async() => {
  const data = {
    user_ids: [checkedRows.value[0].id],
    status: checkedRows.value[0].status === 'enabled' ? 'disabled' : 'enabled'
  }
  await changeUsersStatus(data)
  // 获取用户列表
  getList()
}
// 重置密码
const resetPasswordFun = () => {
  rowData.value = checkedRows.value[0]
  editType.value = 'resetPassword'
  editLayer.value = true
}

// 代管用户
const delegateFun = async(scene) => {
  const user = checkedRows.value[0] || {}
  delegateLoading.value = true
  try {
    const res = await getSysConfig({ name: 'impersonation', display_name: 'impersonation' })
    if (!res.data.metadata[`${scene}_addr`]) {
      ElMessage.error(t(`user.no_${scene}_delegate`))
      return
    }
    // 获取用户信息
    const impersonated = await getDelegateUserInfo(user.id)
    // 如果url末尾有/，则拼接callback, 否则拼接/callback
    const addr = res.data.metadata[`${scene}_addr`]
    const url = addr.endsWith('/')
      ? `${addr}callback?impersonated=${JSON.stringify(impersonated)}`
      : `${addr}/callback?impersonated=${JSON.stringify(impersonated)}`
    delegateLoading.value = false
    window.open(url, '_blank')
  } catch (error) {
    delegateLoading.value = false
  }
}

// 删除用户
const deleteFun = () => {
  let html = ''
  for (const i of checkedRows.value) {
    html += `<p>${i.username}</p>`
  }
  ElMessageBox.confirm(
    `
    ${t('alarm.checked')}${checkedRows.value.length}${t('route.user')}
    ${html}
    `,
    t('user.deleteUserTip'),
    {
      confirmButtonText: t('form.submit'),
      cancelButtonText: t('form.cancel'),
      dangerouslyUseHTMLString: true,
      type: 'warning'
    }
  )
    .then(async() => {
      const ids = []
      checkedRows.value.forEach(user => {
        ids.push(user.id)
      })
      await deleteUsers(ids)
      getList()
    })
}

// table data
const tableData = reactive({
  header: [
    {
      key: 'id',
      value: t('user.user_id'),
      minWidth: '190px',
      sortable: true,
      fixed: 'left'
    },
    {
      key: 'username',
      minWidth: '190px',
      value: t('user.userName'),
      sortable: true
    },
    {
      key: 'email',
      minWidth: '190px',
      value: t('user.email'),
      sortable: true
    },
    {
      key: 'company_name',
      minWidth: '190px',
      value: t('user.company_name'),
      sortable: true
    },
    {
      key: 'display_country',
      minWidth: '190px',
      value: t('user.email_area'),
      sortable: true
    },
    {
      key: 'display_area',
      minWidth: '190px',
      value: t('user.area'),
      sortable: true
    },
    {
      key: 'mobile',
      minWidth: '190px',
      value: t('user.mobile'),
      sortable: true
    },
    {
      key: 'display_roles',
      minWidth: '190px',
      value: t('user.role'),
      sortable: true
    },
    {
      key: 'enterprise_id',
      minWidth: '190px',
      value: t('user.tenantID'),
      sortable: true
    },
    {
      key: 'enterprise_name',
      minWidth: '160px',
      value: t('user.tenantName'),
      sortable: true,
      hoverCopy: true
    },
    {
      key: 'scene',
      value: t('license.scene'),
      minWidth: '160px',
      sortable: true,
      abnormalValue: (row) => sceneMap[row.scene] || t('table.all')
    },
    {
      key: 'display_status',
      value: t('table.status'),
      minWidth: store.state.app.sLanguage === 'en' ? '140px' : '110px',
      sortable: true,
      isShowCircle: true,
      icon: 'status',
      abnormalValue: (row) => row.display_status
    },
    {
      key: 'created_at',
      minWidth: '170px',
      value: t('table.created_at'),
      sortable: true
    }
  ],
  data: listData,
  total: listTotal
})
</script>

<style lang="scss" scoped>
.operate-group {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 16px;
}
.operate-group-left {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.operate-group-right {
  margin-top: 0 !important;
  margin-left: auto;
  flex-wrap: nowrap !important;
}
.operate-group-right :deep(.search-toolbar) {
  flex-wrap: nowrap;
}
</style>
